/**
 * useAudioCommands — the ONE way UI components issue TTS engine commands
 * (plan/overhaul/prep/phase5-tts-strangler.md §5b.4).
 *
 * Returns the bound command functions of the app-wide {@link TtsController}.
 * Components READ playback/settings state from the stores
 * (`useTTSPlaybackStore` / `useTTSSettingsStore` selectors) and WRITE commands
 * through this hook — store actions do not wrap the engine, and
 * `@app/tts/mainThreadAudioPlayer` is import-banned outside src/app/tts/
 * (eslint no-restricted-imports).
 *
 * The returned object is stable for the lifetime of the app (the controller is
 * a singleton with bound command fields), so it is safe in dependency arrays.
 *
 * Component tests mock THIS module (`vi.mock('@app/tts/useAudioCommands')`)
 * instead of reaching for the engine composition root.
 */
import { getTtsController, type TtsController } from './TtsController';

export type AudioCommands = Pick<TtsController,
    | 'play' | 'pause' | 'stop' | 'jumpTo' | 'seek' | 'preview'
    | 'setBookId' | 'loadSectionBySectionId'
    | 'skipToNextSection' | 'skipToPreviousSection'
    | 'loadVoices' | 'downloadVoice' | 'deleteVoice' | 'checkVoiceDownloaded'
    | 'exportDiagnostics' | 'triggerDiagnosticsSnapshot'
    | 'listDiagnosticSnapshots' | 'deleteDiagnosticSnapshot'
    | 'clearDiagnosticSnapshots' | 'shareDiagnosticSnapshot'
>;

export function useAudioCommands(): AudioCommands {
    return getTtsController();
}
